/**
 * Whether phones and tablets are held at the mobile landing page instead of the app.
 * Off while tablet support is built out; set back to true to restore the gate.
 */
export const MOBILE_GATE_ENABLED = false;

/** Primary pointer is a finger. Same query as the Tailwind `touch:` variant. */
export const TOUCH_POINTER_QUERY = '(hover: none) and (pointer: coarse)';

/**
 * Whether the primary pointer can't hover (a phone or tablet without a trackpad).
 * Anything shown on hover elsewhere needs a tap equivalent when this is true.
 */
export function hasTouchPrimaryPointer(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(TOUCH_POINTER_QUERY).matches;
}

/**
 * Whether the app is running on a phone or tablet, as opposed to a laptop or desktop.
 *
 * Viewport width alone can't answer this: an iPad Pro in landscape is wider than
 * many laptops, and a narrowed desktop window is not a phone. So each check asks
 * about the device instead:
 *
 * 1. `userAgentData.mobile` — Chromium's own answer for phones.
 * 2. The UA string — catches phones and tablets on browsers without `userAgentData`
 *    (Safari, Firefox), including Android tablets, which report `mobile: false`.
 * 3. iPadOS 13+ Safari sends a Mac UA by default; a real Mac has no touch points.
 * 4. A touch-only primary input — catches tablets in "desktop site" mode, whose UA
 *    looks like a desktop. Touchscreen laptops still have a trackpad as their
 *    primary pointer, so they don't match.
 */
export function isMobileOrTabletDevice(): boolean {
  if (typeof window === 'undefined') return false;

  const nav = window.navigator as Navigator & { userAgentData?: { mobile: boolean } };
  if (nav.userAgentData?.mobile) return true;

  const ua = nav.userAgent;
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  if (/Macintosh/.test(ua) && nav.maxTouchPoints > 1) return true;

  return hasTouchPrimaryPointer();
}
